import { useRef, useState, forwardRef, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Text from "@commons/Typography/Text";
import { toast } from "react-toastify";
import { compressFiles } from "@utils/compressor";

type UploadInputProps = {
  accept: Array<string>;
  multiple?: boolean;
  maxFiles?: number;
  value?: File[];
  onChange?: (files: File[]) => void;
  onBlur?: () => void;
  name?: string;
};

const UploadInput = forwardRef<HTMLInputElement, UploadInputProps>(
  ({ accept, multiple, maxFiles, value = [], onChange, onBlur, name }, ref) => {
    const [files, setFiles] = useState<Array<{ file: File }>>(
      value.map((file) => ({ file })),
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      setFiles(value.map((file) => ({ file })));
    }, [value]);

    const notifyChange = (newFiles: Array<{ file: File }>) => {
      const fileArray = newFiles.map((f) => f.file);
      onChange?.(fileArray);
    };

    const handleFiles = async (newFiles: FileList | null) => {
      if (!newFiles) return;

      if (maxFiles && files.length + newFiles.length > maxFiles) {
        toast.error(`Você pode enviar no máximo ${maxFiles} arquivos.`);
        return;
      }

      setLoading(true);

      const fileArray = Array.from(newFiles);

      const compressedFiles = await compressFiles(fileArray);

      const updatedFiles = [
        ...files,
        ...Array.from(compressedFiles)
          .filter((f) => !files.some((pf) => pf.file.name === f.name))
          .map((f) => ({ file: f })),
      ];

      setFiles(updatedFiles);
      notifyChange(updatedFiles);

      setLoading(false);
    };

    const handleDelete = (name: string) => {
      setLoading(true);
      const updatedFiles = files.filter((f) => f.file.name !== name);
      setFiles(updatedFiles);
      notifyChange(updatedFiles);
      setLoading(false);
    };

    return (
      <div className="flex flex-wrap gap-4 justify-center">
        <div
          className="border-2 border-dashed border-primary rounded-lg flex flex-col items-center justify-center cursor-pointer w-full min-w-48 max-w-lg min-h-36 p-4 flex-1"
          onClick={() => inputRef.current?.click()}
          onDrop={(e) => {
            e.preventDefault();
            handleFiles(e.dataTransfer.files);
          }}
          onDragOver={(e) => e.preventDefault()}
        >
          <input
            data-testid="file-input"
            type="file"
            multiple={multiple}
            max={maxFiles}
            ref={ref || inputRef}
            name={name}
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            onBlur={onBlur}
            accept={accept?.join(",")}
          />
          <Text
            customVariant="green"
            size="md"
            className="mb-2 font-semibold text-center"
          >
            Upload de anexos
          </Text>
          <Text customVariant="green" className="mb-6 text-center">
            Clique ou arraste os arquivos nessa área
          </Text>
          <Text customVariant="gray" size="sm" className="text-center">
            Arquivos permitidos (
            {accept
              .map((type) => type.replace(/\./g, "").toUpperCase())
              .join(", ")}
            )
          </Text>
        </div>

        <div className="border border-gray-300 shadow-sm rounded-lg bg-white w-full min-w-64 max-w-lg min-h-36 max-h-56 p-4 flex-1 overflow-y-auto relative">
          {files.map((input) => (
            <div
              key={input.file.name}
              className="flex items-center gap-2 py-2 border-b border-gray-300 last:border-b-0 text-sm"
            >
              <InsertDriveFileIcon className="text-accent" />
              <Text customVariant="gray" size="sm" className="flex-1">
                {input.file.name}
              </Text>

              <IconButton
                size="small"
                onClick={() => handleDelete(input.file.name)}
                aria-label="Excluir"
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ))}
          {files.length === 0 && (
            <div className="flex h-full w-full justify-center items-center">
              <Text customVariant="gray" size="sm" className="text-center">
                Nenhum arquivo
              </Text>
            </div>
          )}
          {loading && (
            <div className="absolute inset-0 backdrop-opacity-80 bg-white/50 flex items-center justify-center z-10">
              <CircularProgress className="text-primary" />
            </div>
          )}
        </div>
      </div>
    );
  },
);

UploadInput.displayName = "UploadInput";

export default UploadInput;
